import { BritishColumbiaHealthCardNumberValidator } from "./BritishColumbiaHealthCardNumberValidator";
import { OntarioHealthCardNumberValidator } from "./OntarioOhipNumberValidator";
import { QuebecRamqNumberUtilities } from "./QuebecRamqNumberUtilities";

/**
 * @class Validator
 * @description Class in charge of validating Health Card Numbers depending on region. Scalable to different regions.
 * Must use same country and region codes as in database.
 */
class Validator {

    /**
     * @private
     * @type {Object}
     * @memberof Validator
     * @description 
     *      - textMask used by the application MaskedInput component. 
     *      - regExp: Regular expressio 
     */
    private readonly countryRegionData: object = {
        CA: {
            QC: {
                placeHolder: "____ ____ ____",
                regExp: /^([A-Z]{4})([0-9]{8})$/,
                textMask: "AAAA 1111 1111",
                extraRule: this.quebecRAMQValidation
            },
            NB: {
                regExp: /^([0-9]{3})([0-9]{3}([0-9]{3}))$/,
                textMask: "111-111-111",
                placeHolder: "___-___-___",
                extraRule: null
            },
            ON: {
                regExp: /^([0-9]{4})([0-9]{3})([0-9]{3})([A-Z]{2}){0,1}$/,
                textMask: "1111-111-111-AA",
                placeHolder: "____-___-___-__",
                extraRule: (cardNumber: string) => new OntarioHealthCardNumberValidator().isValid(cardNumber)
            },
            BC: {
                regExp: /^([0-9]{4})([0-9]{3})([0-9]{3})$/,
                textMask: "1111 111 111",
                placeHolder: "____ ___ ___",
                extraRule: (cardNumber: string) => new BritishColumbiaHealthCardNumberValidator().isValid(cardNumber)
            },
            Other: {
                regExp: /^\w*/,
                textMask: null,
                placeHolder: null,
                extraRule: null
            }
        },
        MX: {
            Other: {
                placeHolder: "__________________",
                regExp: /^([A-Z]{4})([0-9]{6})([A-Z]{6})([0-9]{2})$/,
                textMask: "AAAA111111AAAAAA11",
                extraRule: null
            }
        },
        CO: {
            Other: {
                placeHolder: "__________________",
                regExp: /^([A-Z]{4})([0-9]{6})([A-Z]{6})([0-9]{2})$/,
                textMask: "AAAA111111AAAAAA11",
                extraRule: null
            }
        },
        US: {
            Other: {
                regExp: /^\w*/,
                textMask: null,
                placeHolder: null,
                extraRule: null
            }
        },
        Other: {
            regExp: /^\w*/,
            textMask: null,
            placeHolder: null,
            extraRule: null
        }
    }

    public extractCountryAndProvinceFromHealthCardNumberFormat(healthCardNumber: string): { country: string, region: string } {
// tslint:disable-next-line: forin
        for (const country in this.countryRegionData) {
            if (country === "Other") {
                break;
            }
            for (const region in this.countryRegionData[country]) {
                if (this.countryRegionData[country][region]["textMask"] && this.countryRegionData[country][region].regExp && this.countryRegionData[country][region].regExp.test(healthCardNumber)) {
                    return {
                        country,
                        region
                    }
                }
            }
        }
        return null;
    }
    public validateHealthCardNumber(val: string, country: string, region: string): boolean {
        let isValid = true;

        // Remove whitespace, parenthesis, underscore, dash, plus
        val = val.replace(/\s|\(|\)|_|-|\+/g, '');

        // allow an empty NAM to be valid
        if (this.IsNullOrWhiteSpace(val)) {
            return true;
        }
        const validationRegexPattern = this.getCountryAndRegionRegexPattern(country, region);

        isValid = validationRegexPattern.test(val);
        if (isValid) {
            if (this.countryRegionData.hasOwnProperty(country)) {
                if (this.countryRegionData[country].hasOwnProperty(region)) {
                    if (this.countryRegionData[country][region].hasOwnProperty("extraRule") && this.countryRegionData[country][region]["extraRule"]) {
                        return this.countryRegionData[country][region]["extraRule"](val);
                    }
                } else {
                    if (this.countryRegionData[country].hasOwnProperty("extraRule") && this.countryRegionData[country]["extraRule"]) {
                        return this.countryRegionData[country]["extraRule"](val);
                    }
                }
            } else {
                return isValid;
            }
        }

        return isValid;
    }

    public getCountryAndRegionMask(country: string, region: string) {
        return this.getCountryAndRegionProperty(country, region, "textMask");
    }
    public getCountryAndRegionPlaceHolder(country: string, region: string) {
        return this.getCountryAndRegionProperty(country, region, "placeHolder");
    }

    public getCountryAndRegionRegexPattern(country: string, region: string): RegExp {
        return this.getCountryAndRegionProperty(country, region, "regExp");
    }

    public verifyScannedValue(healthCardNumber: string): {country: string, region: string} {
        for (const country of Object.keys(this.countryRegionData)) {
            if (country === "Other") {
                break;
            }
            for (const region of Object.keys(this.countryRegionData[country])) {
                if (region === "Other" && String(this.countryRegionData[country][region].regExp) === String(/^\w*/)) {
                    break;
                }
                if (this.countryRegionData[country][region].regExp.test(healthCardNumber)) {
                    return {country, region};
                }
            }
        }
        return null;
    }

    private quebecRAMQValidation(val: string): boolean {
        return new QuebecRamqNumberUtilities(val).validateRamqNumber();
    }

    private getCountryAndRegionProperty(country: string, region: string, property: string): any {
        try {
            if (this.countryRegionData.hasOwnProperty(country)) {
                if (this.countryRegionData[country].hasOwnProperty(region)) {
                    return this.countryRegionData[country][region][property];
                } else {
                    return this.countryRegionData[country]["Other"][property];
                }
            } else {
                return this.countryRegionData["Other"][property];
            }
        } catch (error) {
            return this.countryRegionData["Other"][property];
        }
    }

    private IsNullOrWhiteSpace(input) {
        if (typeof input === 'undefined' || input == null) { return true; }

        return input.replace(/\s/g, '').length < 1;
    }
}
export default new Validator();


// WEBPACK FOOTER //
// ./src/services/Validator/HealthCard/Validator.ts